import type { FrameworkId, SyncrelEntity, SyncrelRelation, SyncrelState } from "./types";

export const FRAMEWORK_CANVAS_FIELD = "frameworkCanvas";

export const activeFrameworkScope = (state: SyncrelState): FrameworkId | null => (
  state.workspace.activeFramework ?? state.workspace.enabledFrameworks?.[0] ?? null
);

export const entityFrameworkCanvas = (entity: SyncrelEntity): FrameworkId | null => {
  const value = entity.fields[FRAMEWORK_CANVAS_FIELD];
  return typeof value === "string" && value.trim() ? value as FrameworkId : null;
};

export const visibleFrameworkEntities = (state: SyncrelState): SyncrelEntity[] => {
  const active = activeFrameworkScope(state);
  if (!active) return state.entities;

  const scoped = state.entities.filter((entity) => entityFrameworkCanvas(entity) === active);
  if (scoped.length > 0) return scoped;

  return state.entities.filter((entity) => entityFrameworkCanvas(entity) === null);
};

export const visibleFrameworkRelations = (
  state: SyncrelState,
  entities = visibleFrameworkEntities(state),
): SyncrelRelation[] => {
  const visibleIds = new Set(entities.map((entity) => entity.id));
  return state.relations.filter((relation) => (
    visibleIds.has(relation.fromId) && visibleIds.has(relation.toId)
  ));
};

export const frameworkViewState = (state: SyncrelState): SyncrelState => {
  const entities = visibleFrameworkEntities(state);
  const relations = visibleFrameworkRelations(state, entities);
  const visibleEntityIds = new Set(entities.map((entity) => entity.id));
  const visibleRelationIds = new Set(relations.map((relation) => relation.id));
  const selectedEntityId = state.workspace.selectedEntityId && visibleEntityIds.has(state.workspace.selectedEntityId)
    ? state.workspace.selectedEntityId
    : null;
  const selectedRelationId = state.workspace.selectedRelationId && visibleRelationIds.has(state.workspace.selectedRelationId)
    ? state.workspace.selectedRelationId
    : null;

  return {
    ...state,
    workspace: {
      ...state.workspace,
      selectedEntityId,
      selectedRelationId,
    },
    entities,
    relations,
  };
};

export const scopeLegacyEntitiesToFramework = (
  state: SyncrelState,
  frameworkId: FrameworkId,
): SyncrelState => {
  let changed = false;
  const entities = state.entities.map((entity) => {
    if (entityFrameworkCanvas(entity)) return entity;
    changed = true;
    return {
      ...entity,
      fields: {
        ...entity.fields,
        [FRAMEWORK_CANVAS_FIELD]: frameworkId,
      },
    };
  });

  return changed ? { ...state, entities } : state;
};
